/* eslint-disable eqeqeq */
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Col, Form, Button, Image, Container, FormGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import Loading from '../Loading';
import { applyPortalTextColor, dispatchModal } from '../../utils/helper';
import { editContactDetailsService } from '../../services/contactServices';
import { addSignedContractService } from '../../services/signedContractsService';
import { getBookingDisclaimersDetailsService } from '../../services/bookingsService';
import './index.scss';
import { ConfirmationHeader } from '../Reusable';

/**
 * Renders a specific legal section (Disclaimer or Terms)
 * Preserves specific CSS classes for different sections via props.
 */
const LegalDocumentSection = ({ title, htmlContent, inputValue, onChange, settings, containerClass, contentClass }) => (
    <>
        <Form.Label className="text-uppercase payment-form-headding mb-2" style={{ color: settings.textColor }}>
            {title}
        </Form.Label>

        <div className={containerClass}>
            <div className={contentClass}>
                <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </div>

            <div className={containerClass.includes('bottom-section') ? 'mt-4' : ''}>
                <div className="initial-wrapper">
                    <h6 className="initial-text">Initial here to verify you agree to these terms:</h6>
                    <div>
                        <FormGroup>
                            <div className="w-100 initial-input">
                                <Form.Control
                                    type="text"
                                    maxLength={3}
                                    minLength={2}
                                    value={inputValue}
                                    onChange={onChange}
                                />
                            </div>
                        </FormGroup>
                    </div>
                </div>
            </div>
        </div>
    </>
);

/**
 * Generic validation logic for both Disclaimer and Terms.
 * Returns true if the document needs to be displayed/signed.
 */
const validateDocument = (docObj, settings, contact, type = 'disclaimer') => {
    if (_.isEmpty(docObj)) return false;

    const props =
        type === 'disclaimer'
            ? {
                  waive: Number(settings.disclaimerWaiveSign),
                  override: contact.Organization?.overrideDisclaimer === 1,
                  alwaysSign: Number(settings.disclaimerAlwaysSign),
                  lastAcceptedAt: settings.disclaimerLastAcceptedAt,
                  dayExpiration: settings.disclaimerDayExpiration,
                  versionExpiration: settings.disclaimerVersionExpiration == 1,
                  lastAcceptedID: Number(contact.lastAcceptedDisclaimerID),
              }
            : {
                  waive: Number(settings.termsConditionsWaiveSign),
                  override: contact.Organization?.overrideTermsConditions === 1,
                  alwaysSign: Number(settings.termsConditionsAlwaysSign),
                  lastAcceptedAt: settings.termsConditionsAcceptedAt,
                  dayExpiration: Number(settings.termsConditionsDayExpiration),
                  versionExpiration: Number(settings.termsConditionsVersionExpiration) == 1,
                  lastAcceptedID: Number(contact.lastAcceptedTermsConditionsID),
              };

    if (props.waive || props.override) return false;
    if (props.alwaysSign) return true;

    // Check Date Expiration
    if (moment().diff(moment(props.lastAcceptedAt), 'days') > props.dayExpiration) return true;

    // Check Version Expiration
    if (props.versionExpiration && props.lastAcceptedID !== Number(docObj.id)) return true;

    return false;
};

const BookingDesclaimer = () => {
    const history = useHistory();
    const queryObj = useMemo(() => Object.fromEntries(new URLSearchParams(window.location.search)), []);
    const { bid: bookingId, ts } = queryObj;
    const { handleSubmit } = useForm();

    const [state, setState] = useState({
        loading: true,
        showDisclaimer: false,
        disableBtn: true,
        companyId: '',
        bookingCreatedTimeStamp: '',
        bookingDisplayId: '',
        initials: { top: '', bottom: '' },
        validation: { isDisclaimer: false, isTerms: false },
        data: {
            tripSetting: {},
            disclaimer: {},
            termsCondition: {},
            contactDetails: {},
        },
    });
    const { loading, showDisclaimer, disableBtn, bookingDisplayId, validation, data, initials } = state;
    const { tripSetting, disclaimer, termsCondition } = data;

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const booking = await getBookingDisclaimersDetailsService(bookingId, ts);

                if (!isMounted) return;

                if (booking && Object.keys(booking.data).length > 0) {
                    const bookingData = booking.data;
                    const {
                        details,
                        contactDetail: contact,
                        accountSettings: tripSetting,
                        disclaimerData,
                        termsData,
                    } = bookingData;
                    const companyId = details.companyId;

                    applyPortalTextColor(tripSetting?.portalTextColor);

                    // Process Disclaimer Data
                    let disclaimer = {};
                    if (disclaimerData?.length > 0) {
                        const filtered = disclaimerData.filter(
                            (d) => Number(d.companyID) === Number(companyId) && d.isDefault === 1
                        );
                        if (filtered.length > 0) {
                            disclaimer = filtered.reduce((prev, current) => (prev.id > current.id ? prev : current));
                        }
                    }

                    // Process Terms Data
                    let termsCondition = {};
                    if (termsData?.length > 0) {
                        const filtered = termsData.filter(
                            (t) => Number(t.companyID) === Number(companyId) && t.isDefault === 1
                        );
                        if (filtered.length > 0) {
                            termsCondition = filtered.reduce((prev, current) =>
                                prev.id > current.id ? prev : current
                            );
                        }
                    }

                    // Determine Settings Source (Override vs Global)
                    const useContactSettings = contact.overrideSignatureSettings;
                    const settings = {
                        disclaimerLastAcceptedAt: contact?.disclaimerLastAcceptedAt,
                        termsConditionsAcceptedAt: contact?.termsConditionsAcceptedAt,
                        // Disclaimer Props
                        disclaimerDayExpiration: useContactSettings
                            ? contact.disclaimerDayExpiration
                            : tripSetting.disclaimerDayExpiration,
                        disclaimerVersionExpiration: useContactSettings
                            ? contact.disclaimerVersionExpiration
                            : tripSetting.disclaimerVersionExpiration,
                        disclaimerAlwaysSign: useContactSettings
                            ? contact.disclaimerAlwaysSign
                            : tripSetting.disclaimerAlwaysSign,
                        disclaimerWaiveSign: useContactSettings
                            ? contact.disclaimerWaiveSign
                            : tripSetting.disclaimerWaiveSign,
                        // Terms Props
                        termsConditionsDayExpiration: useContactSettings
                            ? contact.termsConditionsDayExpiration
                            : tripSetting.termsConditionsDayExpiration,
                        termsConditionsVersionExpiration: useContactSettings
                            ? contact.termsConditionsVersionExpiration
                            : tripSetting.termsConditionsVersionExpiration,
                        termsConditionsAlwaysSign: useContactSettings
                            ? contact.termsConditionsAlwaysSign
                            : tripSetting.termsConditionsAlwaysSign,
                        termsConditionsWaiveSign: useContactSettings
                            ? contact.termsConditionsWaiveSign
                            : tripSetting.termsConditionsWaiveSign,
                    };

                    // Run Validation
                    const showDisc = validateDocument(disclaimer, settings, contact, 'disclaimer');
                    const showTerms = validateDocument(termsCondition, settings, contact, 'terms');

                    // Navigation Logic or State Update
                    const createdAtTimeStamp = new Date(details.createdAt).valueOf();

                    if (!showDisc && !showTerms) {
                        history.push({
                            pathname: '/p/booking-confirmation',
                            search: `?bid=${bookingId}&ts=${createdAtTimeStamp}`,
                        });
                    } else {
                        setState((prev) => ({
                            ...prev,
                            loading: false,
                            showDisclaimer: true,
                            companyId: companyId,
                            bookingDisplayId: details.bookingId,
                            bookingCreatedTimeStamp: createdAtTimeStamp,
                            validation: { isDisclaimer: showDisc, isTerms: showTerms },
                            data: { tripSetting, disclaimer, termsCondition, contactDetails: contact },
                        }));
                    }
                } else {
                    dispatchModal('Booking Record Not Found.');
                    setState((prev) => ({ ...prev, loading: false }));
                }
            } catch (e) {
                console.error('Error fetching disclaimer details:', e);
                setState((prev) => ({ ...prev, loading: false }));
            }
        };

        fetchData();
        return () => {
            isMounted = false;
        };
    }, [bookingId, ts, history]);

    // Validation Effect for Button Enable/Disable
    useEffect(() => {
        const { isDisclaimer, isTerms } = validation;
        const topValid = initials.top.length >= 2;
        const bottomValid = initials.bottom.length >= 2;

        let shouldDisable = true;

        if (isDisclaimer && isTerms) {
            shouldDisable = !(topValid && bottomValid);
        } else if (isDisclaimer) {
            shouldDisable = !topValid;
        } else if (isTerms) {
            shouldDisable = !bottomValid;
        }

        setState((prev) => (prev.disableBtn !== shouldDisable ? { ...prev, disableBtn: shouldDisable } : prev));
    }, [initials, validation]);

    const handleInputChange = (key, value) => {
        setState((prev) => ({
            ...prev,
            initials: { ...prev.initials, [key]: value },
        }));
    };

    const handleSave = async () => {
        await handleSubmit(async () => {
            try {
                setState((prev) => ({ ...prev, loading: true }));
                const { data, companyId, initials, bookingCreatedTimeStamp } = state;
                const { contactDetails, disclaimer, termsCondition } = data;

                let signedContract = null;
                let contactUpdate = null;
                const timestamp = moment.utc().format('YYYY-MM-DDTHH:mm:00');
                const localTimestamp = moment().format('YYYY-MM-DDTHH:mm:00');

                // Determine logic path based on what is shown/required
                const needsDisc =
                    !_.isEmpty(disclaimer) &&
                    contactDetails.overrideDisclaimer === 0 &&
                    Number(contactDetails.lastAcceptedDisclaimerID) !== Number(disclaimer.id);
                const needsTerms =
                    !_.isEmpty(termsCondition) &&
                    contactDetails.overrideTermsConditions === 0 &&
                    Number(contactDetails.lastAcceptedTermsConditionsID) !== Number(termsCondition.id);

                if (needsDisc && needsTerms) {
                    // Both needed
                    signedContract = {
                        bookingID: bookingId,
                        disclaimerID: disclaimer.id,
                        disclaimerInitials: initials.top,
                        disclaimerInitialedAt: timestamp,
                        termsConditionsID: termsCondition.id,
                        termsConditionsInitials: initials.bottom,
                        termsConditionsInitialedAt: timestamp,
                    };
                    contactUpdate = {
                        lastAcceptedDisclaimerID: disclaimer.id,
                        disclaimerLastAcceptedAt: localTimestamp,
                        lastAcceptedTermsConditionsID: termsCondition.id,
                        termsConditionsAcceptedAt: localTimestamp,
                        overrideDisclaimer: contactDetails.overrideDisclaimer,
                        overrideTermsConditions: contactDetails.overrideTermsConditions,
                    };
                } else if (needsDisc) {
                    // Only Disclaimer needed (Terms auto-accepted or not needed)
                    signedContract = {
                        disclaimerID: disclaimer.id,
                        disclaimerInitials: initials.top,
                        disclaimerInitialedAt: timestamp,
                        termsConditionsAutoAccepted: 1,
                        termsConditionsAutoAcceptedAt: timestamp,
                    };
                    contactUpdate = {
                        lastAcceptedDisclaimerID: disclaimer.id,
                        termsConditionsAcceptedAt: localTimestamp,
                        overrideDisclaimer: contactDetails.overrideDisclaimer,
                        overrideTermsConditions: contactDetails.overrideTermsConditions,
                    };
                } else if (needsTerms) {
                    // Only Terms needed (Disclaimer auto-accepted or not needed)
                    signedContract = {
                        termsConditionsID: termsCondition.id,
                        termsConditionsInitials: initials.bottom,
                        termsConditionsInitialedAt: timestamp,
                        disclaimerAutoAccepted: 1,
                        disclaimerAutoAcceptedAt: timestamp,
                    };
                    contactUpdate = {
                        lastAcceptedTermsConditionsID: termsCondition.id,
                        disclaimerLastAcceptedAt: localTimestamp,
                        overrideDisclaimer: contactDetails.overrideDisclaimer,
                        overrideTermsConditions: contactDetails.overrideTermsConditions,
                    };
                }

                if (contactUpdate) {
                    contactUpdate.companyId = companyId;
                    await editContactDetailsService(contactUpdate, contactDetails.id);
                }

                if (signedContract) {
                    const newContract = await addSignedContractService(signedContract, companyId);
                    history.push({
                        pathname: '/p/booking-confirmation',
                        search: `?bid=${bookingId}&ts=${bookingCreatedTimeStamp}`,
                        state: { signedContractId: newContract?.data?.id },
                    });
                } else {
                    setState((prev) => ({ ...prev, loading: false }));
                }
            } catch (e) {
                console.error('Error saving contract', e);
                setState((prev) => ({ ...prev, loading: false }));
            }
        })();
    };

    if (!showDisclaimer) return loading ? <Loading loading={loading} /> : null;

    return (
        <div>
            <div className="top-header">
                <ConfirmationHeader tripSetting={tripSetting} bookingDisplayId={bookingDisplayId} />

                <div className="background-box-color">
                    <div className="background-wrapper" style={{ backgroundColor: tripSetting.sectionColor }}>
                        <Container fluid>
                            <Col xs={12} className="d-flex justify-content-center p-0">
                                <div className="w-100 payment-form-main">
                                    <div
                                        className="payment-form-desc-main"
                                        style={{ backgroundColor: tripSetting.mainColor }}
                                    >
                                        {validation.isDisclaimer && (
                                            <LegalDocumentSection
                                                title="Disclaimer"
                                                htmlContent={disclaimer.html}
                                                inputValue={initials.top}
                                                onChange={(e) => handleInputChange('top', e.target.value)}
                                                settings={tripSetting}
                                                containerClass="top-section"
                                                contentClass="desclaimer-text portal-text"
                                            />
                                        )}

                                        {validation.isTerms && (
                                            <LegalDocumentSection
                                                title="Terms & Conditions"
                                                htmlContent={termsCondition.html}
                                                inputValue={initials.bottom}
                                                onChange={(e) => handleInputChange('bottom', e.target.value)}
                                                settings={tripSetting}
                                                containerClass="bottom-section terms-section"
                                                contentClass="legal-disclaimer-warpper scroll-icon terms-text break-word"
                                            />
                                        )}
                                    </div>
                                </div>
                            </Col>

                            <div className="btn-wrapper">
                                <div className="back-btn">
                                    <Button disabled={loading}>
                                        <i className="fas fa-fast-backward"></i> Back
                                    </Button>
                                </div>
                                <div className="continue-btn">
                                    <Button
                                        style={{
                                            backgroundColor: disableBtn ? '#646262' : tripSetting.buttonColor,
                                            borderColor: 'transparent',
                                        }}
                                        disabled={disableBtn}
                                        onClick={handleSave}
                                    >
                                        Continue
                                    </Button>
                                </div>
                            </div>
                        </Container>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-end p-4">
                <Image
                    src={'https://storage.googleapis.com/the-bus-network-bucket/default/Powered-By-TBN-Logo.png'}
                    className="m-0 p-0"
                />
            </div>

            {loading && <Loading loading={loading} />}
        </div>
    );
};

export default React.memo(BookingDesclaimer, () => true);
